import { Component, inject, OnInit } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { LoaderService } from '../../../../shared/services/loader/loader.service';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { filter, finalize, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SmeSignupComponent } from '../sme-signup/sme-signup.component';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';
import { ActivatedRoute } from '@angular/router';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, SmeSignupComponent, SvgIconComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent extends DestroyBase implements OnInit {
  authService = inject(AuthService);
  loaderService = inject(LoaderService);
  commonService = inject(CommonService);
  route = inject(ActivatedRoute);

  isSignupMode = false;
  isSMELogin = false;
  showSSOErrorToast = false;

  ngOnInit(): void {
    // Determine login type based on environment configuration
    this.isSMELogin = environment.isSMESite;
    /**
     * This runs when LoginComponent is loaded (AFTER app bootstrap).
     *
     * We ensure that a default clientId exists in localStorage.
     * If user opens app for first time → no clientId → set default based on site type.
     *
     * IMPORTANT:
     * This does NOT initialize Auth0.
     * Auth0 was already initialized earlier during app bootstrap (provideAuth0).
     */
    if (!localStorage.getItem('auth0_client_id')) {
      localStorage.setItem('auth0_client_id', environment.auth.clientId);
    }

    this._checkAuthenticationError();

    const autoLogin = sessionStorage.getItem('auto_login');

    if (autoLogin) {
      sessionStorage.removeItem('auto_login');
      this._triggerLogin();
    } else {
      const autoLoginQuery =
        this.route.snapshot.queryParamMap.get('auto-login') === 'true' ||
        this.route.snapshot.queryParamMap.get('autoLogin') === 'true';
      if (autoLoginQuery) {
        this._triggerLogin();
      }
    }
  }

  logIn(): void {
    const clientId = environment.auth.clientId;

    if (!clientId) {
      this._showToast('Login not configured for this environment', 'error');
      return;
    }

    this._handleLogin(clientId);
  }

  //  Core logic:  Decides whether to reload app or proceed with login
  private _handleLogin(clientId: string): void {
    // Get current clientId used by app
    const currentClientId = localStorage.getItem('auth0_client_id');

    /**
     * CASE 1: User is switching app (CDFI ↔ SME)
     *
     * Example:
     * current = CDFI, clicked = SME
     *
     * → Update clientId
     * → Reload app so Auth0 reinitializes with new clientId
     */
    if (currentClientId !== clientId) {
      // Store new clientId (SME or CDFI)
      localStorage.setItem('auth0_client_id', clientId);

      sessionStorage.setItem('auto_login', 'true');

      /**
       * Reload is REQUIRED because: After reload:
       * → Angular restarts
       * → provideAuth0 runs again
       * → new clientId (SME) is picked
       */
      window.location.reload();

      // Stop execution here (login will NOT happen now)
      return;
    }

    /**
     * CASE 2: Already using correct clientId
     *
     * Example:
     * current = SME, clicked = SME
     *
     * → No reload needed
     * → Proceed to login
     */
    this._triggerLogin();
  }

  // Actually triggers Auth0 login
  private _triggerLogin(): void {
    this.loaderService.show();

    this.authService
      .logIn()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide()),
      )
      .subscribe({
        error: () => {
          this._showToast('Login failed. Please try again.', 'error');
        },
      });
  }

  toggleSignup(): void {
    this.isSignupMode = !this.isSignupMode;
  }

  private _showToast(message: string, type: 'success' | 'error'): void {
    this.commonService.triggerToast({
      type: type === 'success' ? ToastType.Success : ToastType.Error,
      message,
    });
  }

  private _checkAuthenticationError(): void {
    const queryError =
      this.route.snapshot.queryParamMap.get('error') ||
      new URLSearchParams(window.location.search).get('error');

    if (this._isSSOError(queryError)) {
      this._showSSOErrorToast();
    }

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (this._isSSOError(params['error'])) {
          this._showSSOErrorToast();
        }
      });

    this.authService.auth0.error$
      .pipe(
        filter((err): err is Error => !!err),
        takeUntil(this.destroy$),
      )
      .subscribe((err) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errAny = err as any;
        if (
          this._isSSOError(errAny?.error) ||
          this._isSSOError(err?.message) ||
          this._isSSOError(errAny?.error_description)
        ) {
          this._showSSOErrorToast();
        }
      });
  }

  closeSSOErrorToast(): void {
    this.showSSOErrorToast = false;
  }

  private _isSSOError(errorStr: string | null | undefined): boolean {
    if (!errorStr) return false;
    const lower = errorStr.toLowerCase();
    return lower.includes('sso_required') || lower.includes('sso required');
  }

  private _showSSOErrorToast(): void {
    this.showSSOErrorToast = true;
  }
}
